import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { EmailHealthCheck } from './EmailHealthCheck';
import { WelcomeEmailAdmin } from './WelcomeEmailAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SiteModeAdmin({ token }) {
  const [mode, setMode] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cfg = { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/site-mode`, cfg);
      setMode(data.mode);
      setLog(data.log || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load site mode');
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const change = async (next) => {
    const msg = next === 'live'
      ? 'Switch to LIVE? The full site, estimator and Stripe checkout become public again.'
      : 'Switch to FOUNDING? Public visitors will only see the Founding Member page and payments are paused.';
    if (!window.confirm(msg)) return;
    setBusy(true);
    setError('');
    try {
      await axios.put(`${API_URL}/api/admin/site-mode`, { mode: next }, cfg);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to change site mode');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="site-mode-admin">
      <div className="card p-6 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-muted)' }}>SITE_MODE</div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="text-2xl font-bold" style={{ color: 'var(--color-navy)' }} data-testid="site-mode-current">
            {mode === 'founding' ? 'Founding (public site paused)' : mode === 'live' ? 'Live (full site public)' : '—'}
          </div>
          <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--color-border)' }}>
            {['founding', 'live'].map((m) => (
              <button key={m} disabled={busy || mode === m} onClick={() => change(m)} className="px-4 py-2 text-sm font-medium capitalize"
                style={{ background: mode === m ? 'var(--color-navy)' : 'white', color: mode === m ? 'white' : 'var(--color-muted)' }}
                data-testid={`site-mode-${m}-btn`}>{m}</button>
            ))}
          </div>
        </div>
        <p className="text-sm mt-3" style={{ color: 'var(--color-muted)' }}>
          Founding: public visitors see only the Founding Member page and cannot check out. Signed-in admins and testers can still use the full site, including checkout and discount codes.
        </p>
        {error && <div className="p-3 mt-4 rounded-lg text-sm" style={{ background: '#FEE2E2', color: '#B91C1C' }}>{error}</div>}
      </div>

      <WelcomeEmailAdmin token={token} />
      <EmailHealthCheck token={token} />

      <div className="card overflow-x-auto">
        <div className="px-4 py-3 font-semibold" style={{ color: 'var(--color-navy)' }}>Change log</div>
        <table className="w-full text-sm" data-testid="site-mode-log">
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              {['When', 'Who', 'From', 'To'].map((h) => <th key={h} className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--color-muted)' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {log.map((l, i) => (
              <tr key={i} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="px-4 py-2">{new Date(l.changed_at).toLocaleString('en-GB')}</td>
                <td className="px-4 py-2">{l.changed_by_name || l.changed_by}</td>
                <td className="px-4 py-2 capitalize">{l.from}</td>
                <td className="px-4 py-2 capitalize">{l.to}</td>
              </tr>
            ))}
            {!log.length && <tr><td colSpan={4} className="px-4 py-6 text-center" style={{ color: 'var(--color-muted)' }}>No changes yet. Default is founding.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
